/**
 * Holographic docking guides (M2 spec §7.2, plan §6 M2-20): while the player holds clearance (docking phase
 * `cleared` or `autodock`) for a station whose bay exists, additive emissive rings every `corridor.ringSpacing` m
 * along the approach corridor (M2-11 `corridorRing`, radius `corridorRadius`), green while the ship is within the
 * speed limit that applies and amber past it (style guide §3.6 E1 signal colours; the HUD repeats the warning by
 * shape, §9.5), a pad target in the bay and four light strips along the bay's edges.
 *
 * Three `InstancedMesh`es (rings, pad rings, strips) share one additive unlit material each; colours are linear HDR
 * values in `instanceColor` (colour × intensity). Matrices are in render space (D-011).
 * Building allocates (once); `frameGuides` is allocation-free (D-014).
 */
import {
  AdditiveBlending,
  BoxGeometry,
  DynamicDrawUsage,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  MeshBasicNodeMaterial,
  Quaternion,
  TorusGeometry,
  Vector3,
  type BufferGeometry,
  type Object3D,
} from 'three/webgpu'
import type { Vec3 } from '../../core/math'
import type { DockingConfig } from '../../data/m2Schemas'
import { corridorRadius, corridorRing, corridorRingCount } from '../../docking/corridor'
import type { BayFrame, DockingReadout } from '../../space/contracts'
import { linearColor, type GuideConfig } from '../spacefx/config'
import { writeTRS } from './transform'

/** Guide colour states (index into `GuideView.colors`). */
export const GUIDE_COLOR = { ok: 0, warn: 1, pad: 2, strip: 3 } as const

export interface GuideView {
  readonly root: Group
  readonly rings: InstancedMesh
  readonly pads: InstancedMesh
  readonly strips: InstancedMesh
  /** Linear HDR RGB per `GUIDE_COLOR` (3 floats each). */
  readonly colors: Float32Array
  readonly ringCount: number
  /** The guides are drawn this frame. */
  visible: boolean
  /** The rings show the over-limit colour this frame. */
  warn: boolean
}

function guideMaterial(name: string): MeshBasicNodeMaterial {
  const m = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  m.name = name
  return m
}

function guideMesh(geometry: BufferGeometry, name: string, capacity: number): InstancedMesh {
  const mesh = new InstancedMesh(geometry, guideMaterial(name), capacity)
  mesh.name = name
  mesh.instanceMatrix.setUsage(DynamicDrawUsage)
  const c = new InstancedBufferAttribute(new Float32Array(capacity * 3), 3)
  c.setUsage(DynamicDrawUsage)
  mesh.instanceColor = c
  mesh.count = 0
  mesh.frustumCulled = false
  mesh.matrixAutoUpdate = false
  mesh.renderOrder = 2
  return mesh
}

export function createGuideView(cfg: GuideConfig, docking: Pick<DockingConfig, 'corridor'>, parent: Object3D): GuideView {
  const ringCount = corridorRingCount(docking)
  const [radial, tubular] = cfg.ringSegments
  const root = new Group()
  root.name = 'm2:dockingGuides'
  root.matrixAutoUpdate = false
  root.visible = false
  const rings = guideMesh(new TorusGeometry(1, cfg.ringTubeRatio, radial, tubular), 'm2:guideRings', Math.max(1, ringCount))
  const pads = guideMesh(new TorusGeometry(1, cfg.padTubeRatio, radial, tubular), 'm2:guidePad', cfg.padRadius.length)
  const strips = guideMesh(new BoxGeometry(1, 1, 1), 'm2:guideStrips', 4)
  root.add(rings, pads, strips)
  parent.add(root)
  const colors = new Float32Array(4 * 3)
  linearColor(cfg.okColor, cfg.okIntensity, colors, GUIDE_COLOR.ok * 3)
  linearColor(cfg.warnColor, cfg.warnIntensity, colors, GUIDE_COLOR.warn * 3)
  linearColor(cfg.padColor, cfg.padIntensity, colors, GUIDE_COLOR.pad * 3)
  linearColor(cfg.stripColor, cfg.stripIntensity, colors, GUIDE_COLOR.strip * 3)
  return { root, rings, pads, strips, colors, ringCount, visible: false, warn: false }
}

/** The speed limit the guides compare against: the one that applies here, else the corridor's (on approach). */
export function guideSpeedLimit(readout: Readonly<DockingReadout>, docking: Pick<DockingConfig, 'speedLimits'>): number {
  return readout.speedLimit > 0 ? readout.speedLimit : docking.speedLimits.corridor
}

/** True when the ship is past the limit that applies (the rings turn amber). */
export function guideWarn(readout: Readonly<DockingReadout>, docking: Pick<DockingConfig, 'speedLimits'>): boolean {
  return readout.speed > guideSpeedLimit(readout, docking)
}

/** The guides show while the player holds clearance for a station (not docked, not in open space). */
export function guidesActive(readout: Readonly<DockingReadout>): boolean {
  return (readout.phase === 'cleared' || readout.phase === 'autodock') && readout.station >= 0
}

// Module scratch (D-014).
const basis = new Matrix4()
const bx = new Vector3()
const by = new Vector3()
const bz = new Vector3()
const rot = new Quaternion()
const at: Vec3 = { x: 0, y: 0, z: 0 }

function writeInstance(mesh: InstancedMesh, i: number, x: number, y: number, z: number, sx: number, sy: number, sz: number): void {
  writeTRS(mesh.instanceMatrix.array as Float32Array, i * 16, x, y, z, rot.x, rot.y, rot.z, rot.w, sx, sy, sz)
}

function writeColor(mesh: InstancedMesh, i: number, colors: Float32Array, state: number): void {
  const attr = mesh.instanceColor
  if (!attr) return
  const c = attr.array as Float32Array
  c[i * 3] = colors[state * 3]
  c[i * 3 + 1] = colors[state * 3 + 1]
  c[i * 3 + 2] = colors[state * 3 + 2]
}

function finish(mesh: InstancedMesh, n: number): void {
  mesh.count = n
  mesh.visible = n > 0
  mesh.instanceMatrix.needsUpdate = true
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true
}

/**
 * Lays out the guides of `frame` for this frame (render origin `cam`), or hides them when `frame` is null or the
 * readout holds no clearance. Allocation-free.
 */
export function frameGuides(
  view: GuideView,
  frame: Readonly<BayFrame> | null,
  readout: Readonly<DockingReadout>,
  docking: Pick<DockingConfig, 'corridor' | 'speedLimits'>,
  cfg: GuideConfig,
  cam: Vec3,
): void {
  if (!frame || !guidesActive(readout)) {
    view.visible = false
    view.root.visible = false
    return
  }
  view.visible = true
  view.root.visible = true
  // The bay's rotation: local +X right, +Y up, +Z = −out (the socket's frame), so the torus axis (Z) runs along the corridor.
  bx.set(frame.right.x, frame.right.y, frame.right.z)
  by.set(frame.up.x, frame.up.y, frame.up.z)
  bz.set(-frame.out.x, -frame.out.y, -frame.out.z)
  basis.makeBasis(bx, by, bz)
  rot.setFromRotationMatrix(basis)

  const warn = guideWarn(readout, docking)
  view.warn = warn
  const ringState = warn ? GUIDE_COLOR.warn : GUIDE_COLOR.ok
  const radius = corridorRadius(frame, docking)
  const rings = view.ringCount
  for (let k = 0; k < rings; k++) {
    corridorRing(frame, docking, k, at)
    writeInstance(view.rings, k, Math.fround(at.x - cam.x), Math.fround(at.y - cam.y), Math.fround(at.z - cam.z), radius, radius, radius)
    writeColor(view.rings, k, view.colors, ringState)
  }
  finish(view.rings, rings)

  const px = Math.fround(frame.pad.x - cam.x)
  const py = Math.fround(frame.pad.y - cam.y)
  const pz = Math.fround(frame.pad.z - cam.z)
  const pads = cfg.padRadius.length
  for (let i = 0; i < pads; i++) {
    const r = cfg.padRadius[i]
    writeInstance(view.pads, i, px, py, pz, r, r, r)
    writeColor(view.pads, i, view.colors, GUIDE_COLOR.pad)
  }
  finish(view.pads, pads)

  const hw = frame.width * 0.5
  const hh = frame.height * 0.5
  const w = cfg.stripWidth
  for (let i = 0; i < 4; i++) {
    const sr = i === 0 || i === 3 ? -hw : hw
    const su = i < 2 ? -hh : hh
    const x = frame.center.x + frame.right.x * sr + frame.up.x * su
    const y = frame.center.y + frame.right.y * sr + frame.up.y * su
    const z = frame.center.z + frame.right.z * sr + frame.up.z * su
    writeInstance(view.strips, i, Math.fround(x - cam.x), Math.fround(y - cam.y), Math.fround(z - cam.z), w, w, frame.depth)
    writeColor(view.strips, i, view.colors, GUIDE_COLOR.strip)
  }
  finish(view.strips, 4)
}

export function disposeGuideView(view: GuideView): void {
  view.root.removeFromParent()
  const meshes = [view.rings, view.pads, view.strips]
  for (const mesh of meshes) {
    mesh.geometry.dispose()
    ;(mesh.material as MeshBasicNodeMaterial).dispose()
    mesh.dispose()
  }
}
